import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { CompaniesDataService, CompaniesColumnsService } from '../../services';
import { DialogCompanyFormComponent } from './company-form/company-form.component';
import { DialogCompanyDetailsComponent } from './company-details/company-details.component';
import { Company, CompaniesResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-companies',
  templateUrl: './companies.component.html',
  styleUrls: ['./companies.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class CompaniesComponent implements OnInit, OnDestroy {

  private companyAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private companyEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private companyDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public companyToDelete: Company = {};
  public companies: Company[] = [];
  public company: Company = {};
  public selectedCompanies: Company[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private companiesDataService: CompaniesDataService,
    private companiesColumnsService: CompaniesColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('CompaniesComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Companies - Management');
    this.primengConfig.ripple = true;
    this.getCompanies();
  }

  ngOnDestroy(): void {
    console.log('CompaniesComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getCompanies(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.companiesDataService.getCompanies(this.parameters).subscribe((response: CompaniesResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.companies = response.results;
        } else {
          this.companies = this.companies.concat(response.results);
        }
        if (response.inlineCount > this.companies.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getCompanies();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('COMPANIESNOTLOADED')});
    });
  }

  importCompanies(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.companiesDataService.addCompanies(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('COMPANIESCREATED')});
                if (proceed === j) {
                  this.getCompanies();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('COMPANIESNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('COMPANIESNOTCREATED') + ' ' + msg});
            });
          } else {
            this.companiesDataService.updateCompanies(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('COMPANIESUPDATED')});
                if (proceed === j) {
                  this.getCompanies();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('COMPANIESNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('COMPANIESNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.companiesColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('companies.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedCompanies() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Companies?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.companiesDataService.deleteCompanies(this.selectedCompanies).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Companies Deleted', life: 3000});
            this.getCompanies();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Companies not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Companies not deleted'});
        });
        this.selectedCompanies = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.companies, this.columns, 'Companies');
  }

  exportExcel() {
    ToolsService.exportExcel(this.companies, this.columns, 'Companies');
  }

  exportCsv() {
    ToolsService.exportCsv(this.companies, this.columns, 'Companies');
  }

  refresh() {
    this.companiesDataService.clearCache();
    this.getCompanies();
  }

  companyAdd(): void {
    if (!this.navigation.getParamValue('companies.addInDialog', true)) {
      this.router.navigate(['/companies/add']);
      return;
    }
    this.companyAddDialog = this.dialogService.open(DialogCompanyFormComponent, {
      header: '', // this.translate.instant('NEWCOMPANY'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        company: {id: null},
        users: this.companiesColumnsService.users,
      },
    });

    this.companyAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'Company added'});
        this.getCompanies();
      }
    });
  }

  companyEdit(company: Company): void {
    if (!this.navigation.getParamValue('companies.editInDialog', true)) {
      this.router.navigate(['/companies/edit/' + company.id]);
      return;
    }
    this.companyEditDialog = this.dialogService.open(DialogCompanyFormComponent, {
      header: '', // this.translate.instant('EDITCOMPANY') + ' ' + company.name,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        company: company,
        users: this.companiesColumnsService.users,
      },
    });

    this.companyEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'Company updated'});
        this.getCompanies();
      }
    });
  }

  companyDetails(company: Company): void {
    if (!this.navigation.getParamValue('companies.detailsInDialog', true)) {
      this.router.navigate(['/companies/details/' + company.id]);
      return;
    }
    this.companyDetailsDialog = this.dialogService.open(DialogCompanyDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        company: company,
      }
    });
  }

  companyDelete(company: Company): void {
    this.companyToDelete = company;
    this.messageService.clear();
    this.messageService.add({
      key: 'companyDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + company.name + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.companiesDataService.deleteCompany(this.companyToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Company deleted'});
      this.getCompanies();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Company not deleted'});
    });
  }

  onDeleteReject(): void {
    this.companyToDelete = {};
    this.messageService.clear('companyDelete');
  }

}
